/**
 * What counts against a free account's three sessions, in one place.
 *
 * A free session is USED once its summary has been written: by then the person
 * has received what the session gives, whether or not they ever press the last
 * button. Counting `is_complete` instead (which now means "the person left
 * Close", #71) would let someone read the summary, close the tab, and never be
 * counted, never start the 72-hour wait, and be offered a fresh session again.
 *
 * `is_complete` keeps its own job: it decides whether a session is offered for
 * resume. The server gate (facilitate), the reminder (notifications), the
 * session page and Home must all agree with this file.
 */

export const FREE_SESSION_LIMIT = 3
export const FREE_COOLDOWN_MS = 72 * 60 * 60 * 1000

/** The fields the rule reads. */
export type CountableSession = { session_summary: string | null; created_at: string }

/** Whether the session has reached its summary. */
export function isUsed(s: Pick<CountableSession, 'session_summary'>): boolean {
  return !!s.session_summary
}

/**
 * The free tier's position from a person's recent sessions, newest first.
 * Pass at least the last FREE_SESSION_LIMIT sessions that reached a summary.
 */
export function freeTierState(sessions: readonly CountableSession[], now: Date = new Date()) {
  const used = sessions.filter(isUsed)
  const full = used.length >= FREE_SESSION_LIMIT
  let cooldownUntil: Date | null = null
  if (used.length > 0 && !full) {
    const end = new Date(new Date(used[0]!.created_at).getTime() + FREE_COOLDOWN_MS)
    if (now < end) cooldownUntil = end
  }
  return { used: used.length, full, cooldownUntil }
}
